import { Component, OnChanges, OnInit, SimpleChanges, input, output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import WhitelabelAndroidConfigFactory from '../../factories/whitelabel-android-config.factory';
import AzureService from 'src/app/shared/services/azure.service';
import WhitelabelUploadHelper from '../../helpers/upload.helper';
import { ANDROID_CREDENTIALS_DEFAULT_FOLDER, ANDROID_GOOGLE_SERVICES_JSON, ANDROID_ICONS_DEFAULT_FOLDER, WhitelabelAndroidConfig, WhitelabelAndroidFullIconsName, WhitelabelAndroidIconsDestination, WhitelabelAndroidIconsFolder, WhitelabelAndroidIconsName } from '../../models/whitelabel-android-config';
import { Observable, Subject, forkJoin, map } from 'rxjs';
import { LOGO_DEFAULT_NAME, WhitelabelConfiguration } from '../../models/whitelabel-config';

import * as xml2js from 'xml2js';
import { WhitelabelService } from '../../services/whitelabel.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { fork } from 'child_process';
import { prepareIconsLinksToForm } from '../../helpers/file.helper';

@Component({
  standalone: false,
  selector: 'app-whitelabel-android-component',
  templateUrl: './whitelabel-android.component.html',
  styleUrls: ['./whitelabel-android.component.css']
})
export class WhitelabelAndroidComponent implements OnInit, OnChanges {

  readonly parentForm = input.required<FormGroup>();

  readonly isAndroidChecked = input.required<boolean>();

  readonly savedConfigurations = input<WhitelabelConfiguration>();

  readonly isDraftMode = input.required<boolean>();
  
  readonly updated = output<WhitelabelAndroidConfig>();

  public androidForm: FormGroup;

  public initialAndroidForm: FormGroup;

  public whitelabelUploadHelper!: WhitelabelUploadHelper;

  public tabs: 0 | 1 = 0;

  constructor(private formBuilder: FormBuilder, private azureService: AzureService, private whitelabelService: WhitelabelService, private toastrService: ToastrService) {
    this.whitelabelUploadHelper = new WhitelabelUploadHelper(this.azureService);

    this.androidForm = this.formBuilder.group({
      packageName: new FormControl(null, Validators.required),
      status: new FormControl(0, Validators.required),
      sha256CertificateFingerPrint: new FormControl(null, []),

      keytoolLink: new FormControl(null, Validators.required),
      storeLink: new FormControl(null, Validators.required),
      iconsLinks: new FormControl(null, Validators.required),
      appIconLink: new FormControl(null, Validators.required),
      googleServicesJsonLink: new FormControl(null, Validators.required),

      serviceAccountEmail: new FormControl(null),
      serviceAccountPrivateKey: new FormControl(null)
    });

    this.initialAndroidForm = this.formBuilder.group({
      packageName: new FormControl(null, Validators.required),
      status: new FormControl(0, Validators.required),
      sha256CertificateFingerPrint: new FormControl(null, []),

      keytoolLink: new FormControl(null, Validators.required),
      storeLink: new FormControl(null, Validators.required),
      iconsLinks: new FormControl(null, Validators.required),
      appIconLink: new FormControl(null, Validators.required),
      googleServicesJsonLink: new FormControl(null, Validators.required),

      serviceAccountEmail: new FormControl(null),
      serviceAccountPrivateKey: new FormControl(null)
    });

    this.androidForm.valueChanges.subscribe((data: WhitelabelAndroidConfig) => this.updated.emit(data));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { isAndroidChecked, savedConfigurations, isDraftMode } = changes;
    
    if (isAndroidChecked && !isAndroidChecked.currentValue) {
      this.parentForm().removeControl('androidForm');
    } 
    else if (isAndroidChecked && isAndroidChecked.currentValue) {
      this.parentForm().addControl('androidForm', this.androidForm);
    }

    if (isDraftMode && isDraftMode.currentValue) {
      this.handleEnableDraftModeControls();
    }
    else if (isDraftMode && !isDraftMode.currentValue) {
      this.handleDisableDraftModeControls();
    }

    if (this.parentForm().controls['androidForm'] && savedConfigurations?.currentValue) {
      const _savedConfigurations = savedConfigurations.currentValue as WhitelabelConfiguration;

      this.updateAndroidValuesWithPreviousSavedConfigurations(_savedConfigurations);
     
    }
  }
   
  ngOnInit(): void {
    this.handleAccountantId();
  }

  private handleAccountantId() {
    this.parentForm().controls['accountantId'].valueChanges.subscribe({
      next: () => {
        if (!this.parentForm().controls['accountantId'].valid) this.tabs = 0;
      }
    });
  }

  public onIconsUploadSuccess(files: File[] | File) {
    if (Array.isArray(files)) {
      const iconsDestinations: WhitelabelAndroidIconsDestination = WhitelabelAndroidConfig.getIconsDestination(files);

      const prefix = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${ANDROID_ICONS_DEFAULT_FOLDER}`;
      
      let uploadIconObs$: Observable<any>[][] = [];
      let path = '';

      Object.keys(iconsDestinations).forEach(destination => {
        uploadIconObs$.push(this.uploadDensityIcons(iconsDestinations[destination].icons, `${destination}`));
      });

      const recudedObs$: Observable<any>[] = uploadIconObs$.reduce((acc, cur, i) => {
        cur.forEach(obs => {
          acc.push(obs);
        })

        return acc;
      }, []);

      forkJoin(recudedObs$).subscribe({
        next: () => {
          this.toastrService.info('Ícones enviados com sucesso');
          
          Object.keys(iconsDestinations).forEach(destination => {
            iconsDestinations[destination].icons.forEach(icon => {
              path += `${prefix}/${destination}/${icon.name},`;
            });
          });

          this.androidForm.controls['iconsLinks'].patchValue(path);
        }
      });

      return;
    }

    this.uploadToRoot(files).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${files.name}`;
        
        this.androidForm.controls['appIconLink'].patchValue(path);
        this.toastrService.info('Ícone do App enviado com sucesso');
      }
    });
  }

  public onKeytoolUploadSuccess(file: File) {
    this.uploadToCredentialsFolder(file).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${ANDROID_CREDENTIALS_DEFAULT_FOLDER}/${file.name}`;
        this.androidForm.controls['keytoolLink'].patchValue(path);

        this.toastrService.info('Keytool enviado com sucesso');
      }
    });
  }

  public onStoreUploadSuccess(file: File) {
    this.uploadToCredentialsFolder(file).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${ANDROID_CREDENTIALS_DEFAULT_FOLDER}/${file.name}`;
        this.androidForm.controls['storeLink'].patchValue(path);

        this.toastrService.info('Store enviado com sucesso');
      }
    });
  }

  public onGoogleServicesUploadSuccess(file: File) {    
    this.uploadToRoot(file).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${file.name}`;
        this.androidForm.controls['googleServicesJsonLink'].patchValue(path);

        this.toastrService.info('Google Services enviado com sucesso');
      }
    });
  }

  public onFileUploadError() {
    this.toastrService.error('Arquivo inválido');
  }

  private handleDisableDraftModeControls() {
    Object.keys(this.androidForm.controls).forEach(control => {
      if (this.initialAndroidForm.controls[control].hasValidator(Validators.required)) {
        this.androidForm.controls[control].addValidators(Validators.required);
        this.androidForm.controls[control].updateValueAndValidity();
      }
    });

    this.getControl('status')?.enable();
  }

  private handleEnableDraftModeControls() {
    const controlsNameToKeepRequired = ['status'];
    const controls = Object.keys(this.androidForm.controls);

    const controlsToRemoveRequiredValidation = controls.filter(control => !controlsNameToKeepRequired.includes(control));

    controlsToRemoveRequiredValidation.forEach(control => {
      this.androidForm.controls[control].removeValidators(Validators.required);
      this.androidForm.controls[control].updateValueAndValidity();
    });

    this.getControl('status')?.patchValue(0);
    this.getControl('status')?.disable();
  }

  private get accountantId(): string {
    return this.parentForm().controls['accountantId'].value;
  }

  private uploadDensityIcons(icons: File[], densityFolder: string): Observable<any>[] {
    return this.whitelabelUploadHelper.uploadMany(icons, `${this.accountantId}/${ANDROID_ICONS_DEFAULT_FOLDER}/${densityFolder}`);
  }

  private uploadToCredentialsFolder(file: File) {
    const path = `${this.accountantId}/${ANDROID_CREDENTIALS_DEFAULT_FOLDER}`;
    return this.whitelabelUploadHelper.uploadSingle(file, path);
  }

  private uploadToRoot(file: File) {
    const path = this.accountantId;
    return this.whitelabelUploadHelper.uploadSingle(file, path);
  }

  private updateAndroidValuesWithPreviousSavedConfigurations(configurations: WhitelabelConfiguration) {
    this.getControl('packageName')?.setValue(configurations.android.androidPackageName);
    this.getControl('status')?.setValue(configurations.android.status);
    this.getControl('sha256CertificateFingerPrint')?.setValue(configurations.android.sha256CertificateFingerPrint);
    this.getControl('serviceAccountPrivateKey')?.setValue(configurations.android.serviceAccountPrivateKey);
    this.getControl('serviceAccountEmail')?.setValue(configurations.android.serviceAccountEmail);
    
    this.updateFilesLinks();
  }

  //ToDo: Remover método quando todos os apps forem salvos novamente (com a prop. 'files' preenchida)
  private updateFilesLinks() {
    const rootFolder = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}`;

    const credFolder = `${rootFolder}/${ANDROID_CREDENTIALS_DEFAULT_FOLDER}`;
    const iconsFolder = `${rootFolder}/${ANDROID_ICONS_DEFAULT_FOLDER}`

    this.getControl('keytoolLink')?.setValue(`${credFolder}/keytool`);
    this.getControl('storeLink')?.setValue(`${credFolder}/store`);

    this.getControl('appIconLink')?.setValue(`${rootFolder}/logo.png`);
    this.getControl('googleServicesJsonLink')?.setValue(`${rootFolder}/google-services.json`);

    let iconsLinks: string[] = [];

    WhitelabelAndroidIconsFolder.forEach(folder => {
      WhitelabelAndroidIconsName.forEach(iconName => {
        iconsLinks.push(`${iconsFolder}/${folder}/${iconName}`)
      })
    })

    this.getControl('iconsLinks')?.setValue(prepareIconsLinksToForm(iconsLinks));
  }

  public getControl(controlName: string): AbstractControl | null {
    const controlExists = !!this.androidForm.controls[controlName]
    if (!controlExists) return null;
    return this.androidForm.controls[controlName];
  }

}
